// ReviewFixList (job page): what the owner clears before a job that came back from the field is
// completed. The tech's note when they handed it back, on a mark in their pen; then each thing to clear,
// numbered in red with what's wrong in words, what we know under it, and its fix beside it (Links, a
// Segmented "Bill both / Keep in the price"). A cleared one keeps its place with a hatched mark. A line
// says what's already clear; under it the side-by-side (CompareTable) and a foot (Send back to Sam).
// Nothing left: one sentence says so. Reads only --fixes-*.
import type { ReactNode } from 'react';
import './ReviewFixList.css';

export interface ReviewFix {
  id: string;
  /** Its number, as the compare table points at it ("see 1"). */
  n: number;
  title: string;
  detail: string;
  /** Its fix: Links, a Segmented; or words ("The owner’s to fix"). */
  fix?: ReactNode;
  isCleared?: boolean;
}

export interface ReviewFixListProps {
  /** The tech's note when they handed it back. */
  handback?: { by: string; text: string };
  fixes: ReviewFix[];
  /** Said when nothing is left to clear. */
  allClear?: string;
  /** What's already clear, in a line. */
  clearLine?: ReactNode;
  /** The side-by-side, under the list. */
  children?: ReactNode;
  /** Under it all: "Something wrong on site? Send back to Sam". */
  foot?: ReactNode;
}

export function ReviewFixList({ handback, fixes, allClear, clearLine, children, foot }: ReviewFixListProps) {
  const open = fixes.filter((f) => !f.isCleared).length;
  return (
    <div className="fl-fixes">
      {handback ? (
        <figure className="fl-fixes__quote">
          <figcaption className="fl-fixes__by">{handback.by}</figcaption>
          <blockquote className="fl-fixes__words">“{handback.text}”</blockquote>
        </figure>
      ) : null}
      {fixes.length ? (
        <ol className="fl-fixes__list" aria-label={open ? `${open} to clear` : 'Cleared'}>
          {fixes.map((f) => (
            <li key={f.id} className="fl-fixes__item" data-cleared={f.isCleared ? '' : undefined}>
              <span className="fl-fixes__n" aria-hidden="true">
                {f.isCleared ? '' : f.n}
              </span>
              <span className="fl-fixes__what">
                <span className="fl-fixes__title">
                  {f.isCleared ? <span className="fl-fixes__sr">Cleared: </span> : null}
                  {f.title}
                </span>
                <span className="fl-fixes__detail">{f.detail}</span>
              </span>
              {f.fix ? <span className="fl-fixes__fix">{f.fix}</span> : null}
            </li>
          ))}
        </ol>
      ) : null}
      {!open && allClear ? <p className="fl-fixes__line">{allClear}</p> : null}
      {clearLine ? <p className="fl-fixes__line">{clearLine}</p> : null}
      {children}
      {foot ? <div className="fl-fixes__foot">{foot}</div> : null}
    </div>
  );
}
